import { useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import { RenderIf } from 'react-rainbow-components';
import Button from 'components/Button';
import ButtonIcon from 'components/ButtonIcon';
import Close from 'components/icons/close';
import Gradient from 'components/Gradient';
import useSearchFilters from 'hooks/useSearchFilters';
import StatusIcon from 'components/icons/status';
import Calendar from 'components/icons/calendar';
import Accordion from '../../Opportunities/View/accordion';
import {
    FilterHeader,
    Title,
    FiltersContainer,
    FiltersContent,
} from '../../Opportunities/View/styled';
import { StyledWebhookEventsFilter } from './styled';
import RemindersDateFilter from './dateFilter';

interface Params {
    onRequestClose?: () => void;
}

const filterNames = ['status', 'timestamp'];

const Filters = ({
    onRequestClose = () => {},
}: Params) => {
    const {
        values,
        getValue: getFilterValue,
        setValue: setFilterValue,
        clear: clearFilters,
    } = useSearchFilters({ filterNames });

    const activeFiltersCount = useMemo(
        () => filterNames.reduce(
            (count, key) => (
                getFilterValue(key) !== null
                    ? count + 1
                    : count
            ),
            0,
        ),
        [getFilterValue],
    );

    const handleChangeTags = useCallback(
        (newFilterValue: string[] | null) => setFilterValue(
            'status',
            (
                newFilterValue === null
                    ? null
                    : newFilterValue.join(',')
            ),
        ),
        [setFilterValue],
    );

    const statusFilterValue = useMemo(
        () => {
            const { status } = values;
            if (status !== null) return String(status).split(',');
            return null;
        },
        [values],
    );

    const handleChangeDate = useCallback(
        (newFilterValue: Date[]) => setFilterValue(
            'timestamp',
            (
                newFilterValue.length === 0
                    ? null
                    : newFilterValue.map((date) => date.toISOString()).join(',')
            ),
        ),
        [setFilterValue],
    );

    const dateFilterValue = useMemo(
        () => {
            const { timestamp } = values;
            if (!timestamp) return undefined;
            return String(timestamp).split(',').map((strDate) => DateTime.fromISO(strDate).toJSDate());
        },
        [values],
    );

    return (
        <FiltersContainer>
            <FilterHeader>
                <Title>
                    Filters
                    {' '}
                    <RenderIf isTrue={activeFiltersCount > 0}>
                        (
                        {activeFiltersCount}
                        )
                    </RenderIf>
                </Title>
                <div>
                    <Button
                        label="Clear"
                        variant="base"
                        size="small"
                        disabled={!activeFiltersCount}
                        borderRadius="semi-square"
                        onClick={clearFilters}
                    />
                    <ButtonIcon
                        borderRadius="semi-square"
                        size="small"
                        icon={<Close />}
                        onClick={onRequestClose}
                    />
                </div>
            </FilterHeader>
            <FiltersContent>
                <Accordion icon={<Calendar />} label="Date" name="timestamp">
                    <RemindersDateFilter
                        value={dateFilterValue}
                        onChange={handleChangeDate}
                    />
                </Accordion>
                <Accordion icon={<StatusIcon />} label="Status" name="status">
                    <StyledWebhookEventsFilter
                        events={[
                            { name: 'pending', description: 'Pending reminders' },
                            { name: 'scheduled', description: 'Scheduled reminders' },
                            { name: 'due', description: 'Due reminders' },
                            { name: 'completed', description: 'Completed reminders' },
                            { name: 'cancelled', description: 'Cancelled reminders' },
                        ]}
                        value={statusFilterValue}
                        onChange={handleChangeTags}
                    />
                </Accordion>
            </FiltersContent>
            <Gradient position="bottom" />
        </FiltersContainer>
    );
};

export default Filters;
